import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import css from 'classnames'
import { observer } from 'mobx-react-lite'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts'
import { Props } from './types'
import { SubMenuItem } from '../sub-menu-item'
import { ArrowToDown, ToolTip } from '~/shared/ui'
import { useStore } from '~/app/store'
import { useCurrentLegalEntityStores } from '~/shared/lib/hooks/use-current-legal-entity-stores'
import { SIDEBAR_ICONS } from '../../lib/constants/sidebar-icons'
import { ExpressOrderIndicator } from '~/features/express-order-indicator'

export const SubMenu = observer(({ item, isOpen }: Props) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [flyoutPosition, setFlyoutPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const { pathname } = useLocation()
  const submenuRef = useRef<HTMLLIElement | null>(null)
  const { warehousesStore } = useStore()
  const stores = useCurrentLegalEntityStores()

  const toggleSubMenu = () => setExpanded((prev) => !prev)

  const activeLink = item.subLinks?.find((link) => link.link === pathname)

  const isSubLinkActive = !!item.subLinks?.some((l) => {
    if (pathname === l.link) return true
    if (pathname.startsWith(`${l.link}/`)) return true
    return false
  })

  const handleClick = () => {
    if (!isOpen) {
      setFlyoutPosition(null)
      setExpanded((prev) => !prev)
      return
    }

    toggleSubMenu()
  }

  useEffect(() => {
    if (isSubLinkActive && isOpen) {
      setExpanded(true)
    }
  }, [isOpen, isSubLinkActive])

  useUpdateEffect(() => {
    if (!isOpen) {
      setExpanded(false)
    }

    if (isSubLinkActive && isOpen) {
      setExpanded(true)
    }
  }, [isOpen])

  useUpdateEffect(() => {
    if (!pathname.includes(item.link) && !activeLink && !isSubLinkActive) {
      setExpanded(false)
    }
  }, [pathname])

  useEffectOnce(() => {
    if (activeLink && !expanded && isOpen) {
      setExpanded(true)
    }
  })

  useLayoutEffect(() => {
    if (isOpen || !expanded) {
      return
    }

    const rect = submenuRef.current?.getBoundingClientRect()

    if (rect) {
      setFlyoutPosition({
        top: rect.top,
        left: rect.right + 12,
      })
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null

      if (!target || submenuRef.current?.contains(target)) {
        return
      }

      setExpanded(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [expanded, isOpen])

  const triggerNode = (
    <div
      className={css(
        'flex cursor-pointer relative hover:bg-gray-50  p-3 rounded group transition-all text-black-700 items-center gap-4 text-md',
      )}
      onClick={handleClick}
    >
      <span
        className={css(
          'w-[3px] h-4 rounded-md bg-blue-600 absolute left-0 my-auto opacity-0 group-hover:opacity-100 transition-all ',
          {
            hidden: !isOpen,
          },
        )}
      />
      <div className="relative">
        {item?.icon && SIDEBAR_ICONS[item.icon]}
        {item.indicatorKey === 'express' && !isOpen ? (
          <ExpressOrderIndicator
            variant="dot"
            className="absolute -right-1.5 -top-1.5"
          />
        ) : null}
      </div>
      <div
        className={css('flex items-center gap-2', {
          hidden: !isOpen,
        })}
      >
        <p>{t(item.localeTextName) as string}</p>
        {item.indicatorKey === 'express' ? (
          <ExpressOrderIndicator variant="dot" className="relative -top-1" />
        ) : null}
      </div>
      <ArrowToDown
        className={css(
          `ml-auto flex items-center justify-center duration-150 rotate-${
            expanded ? 180 : 0
          }`,
          {
            hidden: !isOpen,
          },
        )}
      />
    </div>
  )

  return (
    <li
      ref={submenuRef}
      className={css({
        'bg-gray-50 rounded relative first:[&>div>span]:text-blue-600 ':
          pathname.includes(item.link) || activeLink || expanded,
      })}
    >
      {!isOpen && !expanded ? (
        <ToolTip
          tooltipText={t(item.localeTextName) as string}
          wrapperClassName="block w-full"
          minWidth="max-content"
          maxWidth="220px"
          placement="right"
        >
          {triggerNode}
        </ToolTip>
      ) : (
        triggerNode
      )}

      {!isOpen && expanded && flyoutPosition ? (
        <div
          className="fixed z-[1001] min-w-[240px] rounded-xl border border-gray-200 bg-white p-3 shadow-2xl"
          style={{
            top: `${flyoutPosition.top}px`,
            left: `${flyoutPosition.left}px`,
          }}
        >
          <div className="flex flex-col gap-1">
            {item.subLinks?.map((subItem) => {
              const isActive =
                pathname === subItem.link ||
                pathname.startsWith(`${subItem.link}/`)

              return (
                <NavLink
                  key={subItem.link}
                  to={subItem.link}
                  onClick={() => setExpanded(false)}
                  className={css(
                    'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-[#242424] transition-colors hover:bg-gray-100',
                    {
                      'bg-gray-100 text-blue-600': isActive,
                    },
                  )}
                >
                  <span className="truncate">
                    {subItem.link === '/stores' && stores.length === 1
                      ? (t('settings') as string)
                      : (t(subItem.localeTextName) as string)}
                  </span>
                  {subItem.badgeKey ? (
                    <span className="relative ml-auto min-w-[24px] rounded-full bg-gray-100 px-2 py-1 text-[11px] leading-none text-[#242424]">
                      {warehousesStore.getStatusCount(subItem.badgeKey)}
                      {subItem.indicatorKey === 'express' ? (
                        <ExpressOrderIndicator
                          variant="dot"
                          className="absolute right-2 top-2"
                        />
                      ) : null}
                    </span>
                  ) : null}
                </NavLink>
              )
            })}
          </div>
        </div>
      ) : null}

      {isOpen ? (
        <SubMenuItem items={item?.subLinks || []} expanded={expanded} />
      ) : null}
    </li>
  )
})
